import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';

const { colors, spacing, common, typography } = THEME;
const MONO = 'monospace';

export const styleSheet = StyleSheet.create({
	// ---- container / states ----
	root: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	stateWrap: {
		alignItems: 'center',
		color: colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: typography.fontSizes.sm,
		justifyContent: 'center',
		minHeight: 160,
		padding: spacing.xl,
		textAlign: 'center',
	},
	stateSpinner: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		minHeight: 160,
	},
	legacyWrap: {
		padding: spacing.md,
	},

	// ---- sticky header ----
	header: {
		backgroundColor: colors.background.light,
		borderBottom: common.border,
		boxShadow: `0 1px 4px ${colors.border.light}`,
		position: 'sticky',
		top: 0,
		zIndex: 2,
	},
	headerTop: {
		alignItems: 'flex-start',
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${spacing.md}px ${spacing.md}px ${spacing.sm}px`,
	},
	eyebrow: {
		color: colors.text.light,
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
	},
	scoreRow: {
		alignItems: 'baseline',
		display: 'flex',
		gap: spacing.sm,
		marginTop: spacing.xs,
	},
	scoreValue: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: 42,
		fontWeight: typography.fontWeights.bold,
		lineHeight: 1,
	},
	scoreOutOf: {
		color: colors.text.light,
		fontSize: typography.fontSizes.md,
	},
	rawLine: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		marginTop: spacing.xs,
	},
	strike: {
		textDecoration: 'line-through',
	},
	closeButton: {
		alignItems: 'center',
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.main}`,
		borderRadius: '50%',
		color: colors.text.main,
		cursor: 'pointer',
		display: 'flex',
		flexShrink: 0,
		height: 32,
		justifyContent: 'center',
		width: 32,
		':hover': {
			backgroundColor: colors.hover.light,
		},
	},
	badgeRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.sm,
		padding: `0 ${spacing.md}px ${spacing.sm}px`,
	},
	badge: {
		alignItems: 'center',
		borderRadius: 14,
		display: 'inline-flex',
		fontSize: typography.fontSizes.xxs,
		fontWeight: typography.fontWeights.bold,
		gap: spacing.xs,
		letterSpacing: '0.03em',
		padding: `3px ${spacing.sm}px`,
	},
	badgeExemplar: {
		backgroundColor: colors.info.light,
		border: `1px solid ${colors.info.main}`,
		color: colors.info.dark,
	},
	badgeDqZero: {
		backgroundColor: colors.danger.light,
		border: `1px solid ${colors.danger.main}`,
		color: colors.danger.dark,
	},
	confidenceWrap: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		gap: spacing.sm,
		justifyContent: 'flex-end',
		minWidth: 120,
	},
	confidenceLabel: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xxs,
	},
	confidenceValue: {
		color: colors.text.main,
		fontSize: typography.fontSizes.xxs,
		fontWeight: typography.fontWeights.bold,
	},
	confidenceTrack: {
		backgroundColor: colors.background.dark,
		borderRadius: 3,
		height: 6,
		overflow: 'hidden',
		width: 70,
	},
	meterFill: {
		borderRadius: 3,
		height: '100%',
		transition: 'width 0.3s',
	},
	breakdownLine: {
		backgroundColor: colors.background.main,
		borderTop: `1px solid ${colors.border.light}`,
		color: colors.text.main,
		display: 'flex',
		flexWrap: 'wrap',
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
		gap: `${spacing.xs}px ${spacing.md}px`,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},

	// ---- scroll body ----
	body: {
		backgroundColor: colors.background.main,
		padding: `${spacing.md}px ${spacing.md}px ${spacing.xxl}px`,
	},
	section: {
		marginBottom: spacing.lg,
	},
	sectionLabel: {
		color: colors.text.light,
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
		letterSpacing: '0.07em',
		marginBottom: spacing.sm,
		textTransform: 'uppercase',
	},
	chipRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.sm,
	},
	chip: {
		alignItems: 'center',
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: 16,
		color: colors.text.light,
		display: 'inline-flex',
		fontSize: typography.fontSizes.xs,
		padding: `5px ${spacing.md}px`,
	},
	chipActive: {
		backgroundColor: colors.primary.main,
		border: `1px solid ${colors.primary.main}`,
		color: colors.primary.contrastText,
		fontWeight: typography.fontWeights.bold,
	},
	note: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		color: colors.text.main,
		fontSize: typography.fontSizes.xs,
		marginTop: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	noteWarning: {
		backgroundColor: colors.warning.light,
		border: `1px solid ${colors.warning.main}`,
		color: colors.warning.dark,
	},

	// ---- per-area ----
	areaList: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	areaCard: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	areaCardNA: {
		opacity: 0.6,
	},
	areaHead: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: spacing.sm,
	},
	areaLabel: {
		color: colors.text.main,
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.bold,
	},
	areaEarned: {
		color: colors.text.main,
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
	},
	areaEarnedNA: {
		color: colors.text.light,
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
	},
	naRow: {
		alignItems: 'center',
		backgroundColor: colors.background.main,
		border: `1px dashed ${colors.border.main}`,
		borderRadius: common.borderRadius,
		color: colors.text.light,
		display: 'flex',
		fontSize: typography.fontSizes.xs,
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.sm}px`,
	},
	areaBarTrack: {
		backgroundColor: colors.background.dark,
		borderRadius: 3,
		height: 6,
		marginBottom: spacing.sm,
		overflow: 'hidden',
	},
	triggerList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 5,
	},
	triggerRow: {
		alignItems: 'center',
		borderRadius: common.borderRadius,
		display: 'flex',
		gap: spacing.sm,
		padding: `6px ${spacing.sm}px`,
	},
	triggerRowFired: {
		backgroundColor: colors.success.light,
		border: `1px solid ${colors.success.main}`,
	},
	triggerRowIdle: {
		backgroundColor: colors.background.main,
		border: `1px solid ${colors.border.light}`,
	},
	triggerIcon: {
		flexShrink: 0,
	},
	triggerLabel: {
		flex: 1,
		fontSize: typography.fontSizes.xs,
	},
	triggerPoints: {
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
	},
	capNote: {
		backgroundColor: colors.warning.light,
		borderRadius: common.borderRadius,
		color: colors.warning.dark,
		fontSize: typography.fontSizes.xxs,
		marginTop: spacing.sm,
		padding: `${spacing.xs}px ${spacing.sm}px`,
	},

	// ---- outcome ladder ----
	outcomeMath: {
		color: colors.text.main,
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
		marginBottom: spacing.sm,
	},
	ladder: {
		display: 'flex',
		flexDirection: 'column',
		gap: 5,
	},
	ladderRow: {
		alignItems: 'center',
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		display: 'flex',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.sm}px`,
	},
	ladderRowActive: {
		backgroundColor: colors.primary.light,
		border: `1px solid ${colors.primary.main}`,
	},
	ladderCheckSpacer: {
		flexShrink: 0,
		width: 15,
	},
	ladderLabel: {
		flex: 1,
		fontSize: typography.fontSizes.xs,
	},
	ladderPoints: {
		fontFamily: MONO,
		fontSize: typography.fontSizes.xs,
	},

	// ---- modifiers / disqualifiers ----
	rowList: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	modRow: {
		alignItems: 'center',
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		display: 'flex',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	modRowSevere: {
		border: `1px solid ${colors.warning.main}`,
	},
	modDot: {
		borderRadius: '50%',
		flexShrink: 0,
		height: 8,
		width: 8,
	},
	modLabel: {
		color: colors.text.main,
		flex: 1,
		fontSize: typography.fontSizes.xs,
	},
	modTag: {
		borderRadius: 12,
		fontSize: typography.fontSizes.xxs,
		fontWeight: typography.fontWeights.bold,
		padding: `2px ${spacing.sm}px`,
	},
	modTagSevere: {
		backgroundColor: colors.warning.light,
		color: colors.warning.dark,
	},
	modTagFlag: {
		backgroundColor: colors.background.dark,
		color: colors.text.light,
	},
	dqRow: {
		alignItems: 'center',
		backgroundColor: colors.danger.light,
		border: `1px solid ${colors.danger.main}`,
		borderRadius: common.borderRadius,
		display: 'flex',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	dqTier: {
		borderRadius: 5,
		flexShrink: 0,
		fontSize: typography.fontSizes.xxs,
		fontWeight: typography.fontWeights.bold,
		letterSpacing: '0.03em',
		padding: `3px ${spacing.xs}px`,
	},
	dqTierA: {
		backgroundColor: colors.danger.main,
		color: colors.danger.contrastText,
	},
	dqTierB: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.danger.main}`,
		color: colors.danger.dark,
	},
	dqLabel: {
		color: colors.text.main,
		flex: 1,
		fontSize: typography.fontSizes.xs,
	},
	dqEffect: {
		color: colors.danger.dark,
		fontFamily: MONO,
		fontSize: typography.fontSizes.xxs,
		fontWeight: typography.fontWeights.bold,
	},

	// ---- effort ----
	effortBox: {
		alignItems: 'center',
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	effortLabel: {
		color: colors.text.main,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
	},
	effortSub: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xxs,
		marginTop: 2,
	},
	effortValue: {
		color: colors.text.light,
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.xl,
		fontWeight: typography.fontWeights.bold,
	},

	// ---- why / evidence ----
	whyBox: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		padding: `${spacing.md}px ${spacing.md}px`,
	},
	whyText: {
		color: colors.text.main,
		fontSize: typography.fontSizes.xs,
		lineHeight: 1.55,
	},
	evidenceDivider: {
		borderTop: `1px solid ${colors.border.light}`,
		marginTop: spacing.md,
		paddingTop: spacing.md,
	},
	evidenceLabel: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xxs,
		letterSpacing: '0.03em',
		marginBottom: spacing.xs,
		textTransform: 'uppercase',
	},
	evidenceText: {
		color: colors.text.main,
		fontSize: typography.fontSizes.xs,
		fontStyle: 'italic',
		lineHeight: 1.6,
	},

	// ---- suggestion axes (coach quality x rep adherence) + game plan pitch ----
	scorePair: {
		display: 'grid',
		gap: spacing.sm,
		gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
	},
	scoreBox: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	scoreBoxLabel: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xxs,
		letterSpacing: '0.03em',
		textTransform: 'uppercase',
	},
	scoreBoxValue: {
		color: colors.text.main,
		fontFamily: MONO,
		fontSize: typography.fontSizes.lg,
		fontVariantNumeric: 'tabular-nums',
		lineHeight: 1.2,
		marginTop: spacing.xs,
	},
	scoreBoxSub: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xxs,
		lineHeight: 1.4,
		marginTop: spacing.xs,
	},
	suggestionRow: {
		backgroundColor: colors.background.light,
		border: `1px solid ${colors.border.light}`,
		borderRadius: common.borderRadius,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	suggestionRowMissed: {
		borderColor: colors.warning.main,
	},
	suggestionHead: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.xs,
	},
	suggestionStep: {
		color: colors.text.main,
		flex: 1,
		fontSize: typography.fontSizes.xs,
		fontWeight: 600,
		minWidth: 90,
	},
	suggestionReasoning: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xs,
		lineHeight: 1.5,
		marginTop: spacing.xs,
	},
	tag: {
		borderRadius: common.borderRadius,
		fontSize: typography.fontSizes.xxs,
		fontWeight: 600,
		letterSpacing: '0.02em',
		padding: `2px ${spacing.xs}px`,
		whiteSpace: 'nowrap',
	},
	tagGood: {
		backgroundColor: colors.success.light,
		color: colors.success.dark,
	},
	tagMissed: {
		backgroundColor: colors.warning.light,
		color: colors.warning.dark,
	},
	tagNeutral: {
		backgroundColor: colors.background.main,
		color: colors.text.light,
	},
});
